import { useState, useEffect, useCallback } from 'react';
import { useGlossary } from '../contexts/GlossaryContext';
import '../styles/NavagrahChakra.css';

// ── Types ────────────────────────────────────────────────────────
interface Planet {
  number: number;
  name: string;
  icon: string;
  challenge: string;
  title: string;
  instruction: string;
  tokens: number;
  card_count: number;
}

interface YogaCard {
  id: string;
  planet_number: number;
  planet: string;
  planet_icon: string;
  challenge: string;
  challenge_title: string;
  instruction: string;
  sutra_id: number;
  sutra_text: string;
  question: string;
  answer: string;
  hint: string;
  tokens_reward: number;
  difficulty: number;
}

type GamePhase = 'home' | 'board' | 'challenge' | 'answer' | 'victory';

interface TokenState { gold: number; danda: number; }
interface GameState {
  phase: GamePhase;
  planets: Planet[];
  currentPlanetIdx: number;  // 0-8
  cycleCount: number;
  card: YogaCard | null;
  nextCard: YogaCard | null;  // pre-fetched for smooth transitions
  hintVisible: boolean;
  answerVisible: boolean;
  tokens: TokenState;
  totalAnswered: number;
  totalCorrect: number;
}

// ── Planet colour map ────────────────────────────────────────────
const PLANET_COLORS: Record<number, string> = {
  1: 'var(--sun-color)',
  2: 'var(--moon-color)',
  3: 'var(--mars-color)',
  4: 'var(--mercury-color)',
  5: 'var(--jupiter-color)',
  6: 'var(--venus-color)',
  7: 'var(--saturn-color)',
  8: 'var(--rahu-color)',
  9: 'var(--ketu-color)',
};

const STORAGE_KEY = 'navagrah_chakra_tokens';

function loadTokens(): TokenState {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) return JSON.parse(raw);
  } catch {}
  return { gold: 0, danda: 0 };
}
function saveTokens(t: TokenState) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(t));
}

// ── Circular board geometry ──────────────────────────────────────
// 9 planets evenly spaced on a circle, starting at top
function planetPosition(index: number, total: number, radius: number) {
  const angleDeg = (index / total) * 360 - 90; // start at top
  const angleRad = (angleDeg * Math.PI) / 180;
  const cx = radius + radius * Math.cos(angleRad);
  const cy = radius + radius * Math.sin(angleRad);
  return { left: `${cx}px`, top: `${cy}px` };
}

// ── Sub-components ───────────────────────────────────────────────

function TokenBar({ tokens, t }: { tokens: TokenState; t: ReturnType<typeof useGlossary>['t'] }) {
  return (
    <div className="nc-token-bar">
      <div className="nc-token gold">🪙 {tokens.gold} {t('nc.token.gold')}</div>
      <div className="nc-token danda">⚡ {tokens.danda} {t('nc.token.danda')}</div>
    </div>
  );
}

function CircularBoard({
  planets,
  activePlanetIdx,
  visitedSet,
  onPlanetClick,
}: {
  planets: Planet[];
  activePlanetIdx: number;
  visitedSet: Set<number>;
  onPlanetClick: (idx: number) => void;
}) {
  const size = 380;
  const radius = size / 2;
  const nodeRadius = radius * 0.72;

  return (
    <div className="nc-board" style={{ width: size, height: size }}>
      <div className="nc-orbit-ring" />
      <div className="nc-board-center">
        <span style={{ fontSize: '2.5rem' }}>🕉️</span>
        <span className="cycle-label">योगसूत्र</span>
      </div>
      {planets.map((pl, idx) => {
        const pos = planetPosition(idx, planets.length, nodeRadius);
        const isActive  = idx === activePlanetIdx;
        const isVisited = visitedSet.has(idx) && !isActive;
        return (
          <div
            key={pl.number}
            className={`nc-planet-node ${isActive ? 'active' : ''} ${isVisited ? 'visited' : ''}`}
            style={{
              left: pos.left,
              top: pos.top,
              '--planet-color': PLANET_COLORS[pl.number],
            } as React.CSSProperties}
            title={pl.title}
            onClick={() => onPlanetClick(idx)}
          >
            <span className="planet-icon">{pl.icon}</span>
            <span className="planet-name">{pl.name}</span>
          </div>
        );
      })}
    </div>
  );
}

function HomeScreen({
  planets,
  tokens,
  cycleCount,
  onStart,
}: {
  planets: Planet[];
  tokens: TokenState;
  cycleCount: number;
  onStart: () => void;
}) {
  const [showInstructions, setShowInstructions] = useState(false);

  return (
    <div className="nc-home">
      <div className="nc-home-intro">
        <div className="big-om">🕉️</div>
        <h2>नवग्रह चक्र — योगसूत्र ज्ञान खेल</h2>
        <p>९ ग्रहों के चक्र में योगसूत्र के पदों का ज्ञान अर्जित करें।<br />
           प्रत्येक ग्रह एक अलग प्रकार की परीक्षा है।</p>
      </div>

      {cycleCount > 0 && (
        <div style={{ textAlign: 'center', color: 'var(--cosmos-muted)', marginBottom: '1rem', fontSize: '.9rem' }}>
          पूर्ण चक्र: <strong style={{ color: 'var(--gold-color)' }}>{cycleCount}</strong>
          &nbsp;|&nbsp; स्वर्ण: <strong style={{ color: 'var(--gold-color)' }}>{tokens.gold}</strong>
          &nbsp;|&nbsp; दंड: <strong style={{ color: 'var(--danda-color)' }}>{tokens.danda}</strong>
        </div>
      )}

      {/* Instructions toggle */}
      <button
        className="nc-instructions-toggle"
        onClick={() => setShowInstructions(v => !v)}
        aria-expanded={showInstructions}
      >
        {showInstructions ? '▲' : '▼'} कैसे खेलें?
      </button>

      {showInstructions && (
        <div className="nc-instructions">
          <ol className="nc-instructions-list">
            <li>
              <span className="nc-instr-icon">🌐</span>
              <span>
                <strong>चक्र-भ्रमण:</strong> ९ ग्रह एक वृत्त में हैं। खेल सूर्य (ग्रह १) से प्रारम्भ होता है और
                क्रमशः सभी ग्रहों से गुज़रते हुए एक पूर्ण चक्र बनाता है।
              </span>
            </li>
            <li>
              <span className="nc-instr-icon">❓</span>
              <span>
                <strong>प्रश्न-पत्र:</strong> प्रत्येक ग्रह पर एक योगसूत्र-आधारित प्रश्न प्रकट होगा।
                सक्रिय ग्रह-चिह्न पर क्लिक करें या <em>"प्रश्न देखें"</em> बटन दबाएँ।
              </span>
            </li>
            <li>
              <span className="nc-instr-icon">💡</span>
              <span>
                <strong>संकेत व उत्तर:</strong> आवश्यकता हो तो पहले संकेत देखें, फिर उत्तर देखें।
                स्वयं मूल्यांकन करें — <em>सही / आधा / गलत</em>।
              </span>
            </li>
            <li>
              <span className="nc-instr-icon">🪙</span>
              <span>
                <strong>मुद्राएँ:</strong> सही उत्तर → <strong style={{ color: 'var(--gold-color)' }}>+पुरस्कार मुद्रा</strong>,
                आधा → +१, गलत → −१ स्वर्ण + १ दंड।
              </span>
            </li>
            <li>
              <span className="nc-instr-icon">♄</span>
              <span>
                <strong>शनि-बाधा (ग्रह ७):</strong> गलत उत्तर पर ग्रह ५ पर वापस जाना पड़ता है।
              </span>
            </li>
            <li>
              <span className="nc-instr-icon">☊</span>
              <span>
                <strong>राहु-दंड (ग्रह ८):</strong> गलत उत्तर पर −२ मुद्रा एवं २ ग्रह पीछे जाना पड़ता है।
              </span>
            </li>
            <li>
              <span className="nc-instr-icon">🏆</span>
              <span>
                <strong>विजय:</strong> सभी ९ ग्रहों को पार करने पर एक चक्र पूर्ण होता है।
                पुनः खेलकर अपनी सटीकता सुधारें।
              </span>
            </li>
          </ol>
        </div>
      )}

      <div className="nc-planets-preview">
        {planets.map(pl => (
          <div key={pl.number} className="nc-preview-cell">
            <div className="picon">{pl.icon}</div>
            <div className="pname">{pl.name}</div>
            <div className="pctype">{pl.title}</div>
          </div>
        ))}
      </div>

      <button className="nc-start-btn" onClick={onStart}>
        चक्र प्रारम्भ करें 🕉️
      </button>
    </div>
  );
}

function ChallengeCard({
  card,
  hintVisible,
  answerVisible,
  onShowHint,
  onShowAnswer,
  onRate,
}: {
  card: YogaCard;
  hintVisible: boolean;
  answerVisible: boolean;
  onShowHint: () => void;
  onShowAnswer: () => void;
  onRate: (rating: 'correct' | 'partial' | 'wrong') => void;
}) {
  const color = PLANET_COLORS[card.planet_number];

  const isShani = card.planet_number === 7;
  const isRahu  = card.planet_number === 8;

  return (
    <div className="nc-challenge-wrapper">
      {/* Planet banner */}
      <div
        className="nc-planet-banner"
        style={{ borderColor: color, background: `${color}18` }}
      >
        <span className="big-icon">{card.planet_icon}</span>
        <div className="banner-text">
          <h3 style={{ color }}>{card.planet} — {card.challenge_title}</h3>
          <p>{card.instruction}</p>
        </div>
      </div>

      <div className="nc-card" style={{ borderColor: `${color}55` }}>
        {/* Special event banners */}
        {isShani && (
          <div className="nc-event-banner shani">
            ♄ शनि-बाधा: सही उत्तर → +{card.tokens_reward} मुद्राएँ | गलत → ग्रह ५ पर वापस
          </div>
        )}
        {isRahu && (
          <div className="nc-event-banner rahu">
            ☊ राहु-दंड: गलत → −२ मुद्राएँ + २ ग्रह पीछे जाएँगे
          </div>
        )}

        <p className="nc-instruction">{card.instruction}</p>
        <p className="nc-question">{card.question}</p>
        <p className="nc-sutra-ref">
          सूत्र {card.sutra_id} — {card.sutra_text}
        </p>

        {/* Hint */}
        {card.hint && !hintVisible && !answerVisible && (
          <button className="nc-reveal-btn" onClick={onShowHint}>
            संकेत देखें 💡
          </button>
        )}
        {card.hint && hintVisible && !answerVisible && (
          <div className="nc-hint-box">💡 {card.hint}</div>
        )}

        {/* Show answer */}
        {!answerVisible && (
          <button
            className="nc-reveal-btn"
            style={{ marginTop: hintVisible ? '.75rem' : '0' }}
            onClick={onShowAnswer}
          >
            उत्तर देखें 👁️
          </button>
        )}

        {/* Answer + self-rating */}
        {answerVisible && (
          <>
            <div className="nc-answer-box" style={{ borderColor: `${color}44` }}>
              <p className="nc-answer-label">सही उत्तर:</p>
              <p className="nc-answer-text" style={{ color }}>{card.answer}</p>
              <p className="nc-full-sutra">सूत्र: {card.sutra_text}</p>
            </div>

            <p className="nc-rate-label">आपका उत्तर कैसा था?</p>
            <div className="nc-rate-btns">
              <button className="nc-rate-btn correct" onClick={() => onRate('correct')}>
                ✅ सही<br />
                <small>+{card.tokens_reward} मुद्रा</small>
              </button>
              <button className="nc-rate-btn partial" onClick={() => onRate('partial')}>
                🤔 आधा<br />
                <small>+1 मुद्रा</small>
              </button>
              <button className="nc-rate-btn wrong" onClick={() => onRate('wrong')}>
                ❌ गलत<br />
                <small>{card.planet_number === 8 ? '−2 मुद्रा' : '−1 मुद्रा'}</small>
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function VictoryScreen({
  cycleCount,
  tokens,
  totalAnswered,
  totalCorrect,
  onContinue,
  onHome,
}: {
  cycleCount: number;
  tokens: TokenState;
  totalAnswered: number;
  totalCorrect: number;
  onContinue: () => void;
  onHome: () => void;
}) {
  const accuracy = totalAnswered > 0 ? Math.round((totalCorrect / totalAnswered) * 100) : 0;
  return (
    <div className="nc-victory">
      <div className="trophy">🏆</div>
      <h2>चक्र {cycleCount} पूर्ण!</h2>
      <p>नौ ग्रहों का ज्ञान-भ्रमण सफलतापूर्वक पूर्ण हुआ।</p>
      <div className="nc-victory-stats">
        <div className="nc-victory-stat">
          <div className="sv">{tokens.gold}</div>
          <div className="sl">🪙 स्वर्ण-मुद्रा</div>
        </div>
        <div className="nc-victory-stat">
          <div className="sv">{accuracy}%</div>
          <div className="sl">सटीकता</div>
        </div>
        <div className="nc-victory-stat">
          <div className="sv">{totalCorrect}/{totalAnswered}</div>
          <div className="sl">सही उत्तर</div>
        </div>
      </div>
      <button className="nc-action-btn" onClick={onContinue} style={{ marginBottom: '.75rem' }}>
        पुनः चक्र करें 🔄
      </button>
      <br />
      <button
        className="nc-action-btn"
        onClick={onHome}
        style={{ background: 'var(--cosmos-card)', color: 'var(--cosmos-text)', border: '1px solid var(--cosmos-border)' }}
      >
        गृह पर जाएँ 🏠
      </button>
    </div>
  );
}

// ── Main component ───────────────────────────────────────────────
export default function NavagrahChakra() {
  const { t } = useGlossary();
  const [state, setState] = useState<GameState>({
    phase: 'home',
    planets: [],
    currentPlanetIdx: 0,
    cycleCount: 0,
    card: null,
    nextCard: null,
    hintVisible: false,
    answerVisible: false,
    tokens: loadTokens(),
    totalAnswered: 0,
    totalCorrect: 0,
  });

  const [visitedSet, setVisitedSet] = useState<Set<number>>(new Set());
  const [loading, setLoading] = useState(true);   // true until planets are fetched
  const [error, setError] = useState<string | null>(null);

  // Load planet metadata on mount
  useEffect(() => {
    fetch('/api/yogagame/planets')
      .then(r => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      })
      .then(d => {
        setState(prev => ({ ...prev, planets: d.planets || [] }));
        setLoading(false);
      })
      .catch(() => {
        setError(t('nc.error.planets'));
        setLoading(false);
      });
  }, [t]);

  const fetchCard = useCallback(async (planetNumber: number): Promise<YogaCard | null> => {
    const res = await fetch(`/api/yogagame/cards?planet=${planetNumber}&limit=1`);
    if (!res.ok) return null;
    const data = await res.json();
    return data.cards?.[0] ?? null;
  }, []);

  const startCycle = useCallback(async () => {
    setLoading(true);
    setError(null);
    const pl = state.planets[0];
    if (!pl) { setLoading(false); return; }
    const card = await fetchCard(pl.number);
    if (!card) { setError(t('nc.error.card')); setLoading(false); return; }
    setState(prev => ({
      ...prev,
      phase: 'board',
      currentPlanetIdx: 0,
      card: null,
      hintVisible: false,
      answerVisible: false,
    }));
    setVisitedSet(new Set());
    setLoading(false);
  }, [state.planets, fetchCard, t]);

  const openChallenge = useCallback(async () => {
    setLoading(true);
    const pl = state.planets[state.currentPlanetIdx];
    if (!pl) return;
    const card = await fetchCard(pl.number);
    if (!card) { setError(t('nc.error.card')); setLoading(false); return; }
    setState(prev => ({
      ...prev,
      phase: 'challenge',
      card,
      hintVisible: false,
      answerVisible: false,
    }));
    setLoading(false);
  }, [state.planets, state.currentPlanetIdx, fetchCard, t]);

  const handleRate = useCallback(async (rating: 'correct' | 'partial' | 'wrong') => {
    const card = state.card;
    if (!card) return;

    const isRahu  = card.planet_number === 8;
    const isShani = card.planet_number === 7;

    let goldDelta = 0;
    let dandaDelta = 0;
    let nextPlanetIdx = (state.currentPlanetIdx + 1) % state.planets.length;

    if (rating === 'correct') {
      goldDelta = card.tokens_reward;
    } else if (rating === 'partial') {
      goldDelta = 1;
    } else {
      dandaDelta = 1;
      goldDelta = isRahu ? -2 : -1;
      if (isRahu) {
        // Move back 2 planets
        nextPlanetIdx = ((state.currentPlanetIdx - 2) + state.planets.length) % state.planets.length;
      }
      if (isShani) {
        // Back to planet 5 (index 4)
        nextPlanetIdx = 4;
      }
    }

    const newTokens: TokenState = {
      gold:  Math.max(0, state.tokens.gold  + goldDelta),
      danda: state.tokens.danda + dandaDelta,
    };
    saveTokens(newTokens);

    // Check if cycle complete (went past planet 9 back to 0)
    const completedCycle = (state.currentPlanetIdx === state.planets.length - 1) &&
                           (rating !== 'wrong' || (!isRahu && !isShani));

    const newVisited = new Set(visitedSet);
    newVisited.add(state.currentPlanetIdx);

    const newAnswered = state.totalAnswered + 1;
    const newCorrect  = state.totalCorrect + (rating === 'correct' ? 1 : 0);

    if (completedCycle) {
      setState(prev => ({
        ...prev,
        phase: 'victory',
        currentPlanetIdx: nextPlanetIdx,
        cycleCount: prev.cycleCount + 1,
        tokens: newTokens,
        card: null,
        nextCard: null,
        totalAnswered: newAnswered,
        totalCorrect: newCorrect,
      }));
    } else {
      setVisitedSet(newVisited);
      // Pre-fetch the next planet's card while user reads the answer
      setLoading(true);
      const nextPl = state.planets[nextPlanetIdx];
      const prefetched = nextPl ? await fetchCard(nextPl.number) : null;
      setState(prev => ({
        ...prev,
        phase: 'answer',
        currentPlanetIdx: nextPlanetIdx,
        tokens: newTokens,
        totalAnswered: newAnswered,
        totalCorrect: newCorrect,
        card: prev.card,        // keep current card visible while user reads answer
        nextCard: prefetched,   // stored properly in state
        hintVisible: prev.hintVisible,
        answerVisible: true,
      }));
      setLoading(false);
    }
  }, [state, visitedSet, fetchCard]);

  const handleNext = useCallback(() => {
    if (state.nextCard) {
      setState(prev => ({
        ...prev,
        phase: 'challenge',
        card: prev.nextCard,
        nextCard: null,
        hintVisible: false,
        answerVisible: false,
      }));
    } else {
      setState(prev => ({ ...prev, phase: 'board', nextCard: null }));
    }
  }, [state.nextCard]);

  const handleReset = useCallback(() => {
    const fresh = { gold: 0, danda: 0 };
    saveTokens(fresh);
    setVisitedSet(new Set());
    setState(prev => ({
      ...prev,
      phase: 'home',
      currentPlanetIdx: 0,
      cycleCount: 0,
      card: null,
      nextCard: null,
      hintVisible: false,
      answerVisible: false,
      tokens: fresh,
      totalAnswered: 0,
      totalCorrect: 0,
    }));
  }, []);

  const goHome = useCallback(() =>
    setState(prev => ({ ...prev, phase: 'home' })), []);

  // ── Render ─────────────────────────────────────────────────────
  const activePlanet = state.planets[state.currentPlanetIdx];

  return (
    <div className="navachakra">
      <div className="nc-header">
        <h1>{t('nc.title')}</h1>
        <p>{t('nc.subtitle')}</p>
      </div>

      {/* Persistent controls: home + reset — shown during active gameplay */}
      {state.phase !== 'home' && !loading && (
        <div className="nc-game-controls">
          <button
            className="ngc-btn"
            onClick={goHome}
            title={t('nc.controls.homeTitle')}
          >
            🏠 <span className="ngc-label">{t('nc.controls.home')}</span>
          </button>
          <span className="ngc-ctrl-title">🕉️ नवग्रह चक्र</span>
          <button
            className="ngc-btn ngc-reset"
            onClick={handleReset}
            title={t('nc.controls.resetTitle')}
          >
            🔄 <span className="ngc-label">{t('nc.controls.reset')}</span>
          </button>
        </div>
      )}

      <TokenBar tokens={state.tokens} t={t} />

      {loading && (
        <div className="nc-loading-screen">
          <div className="nc-spinner" />
          <p>{t('nc.loading')}</p>
        </div>
      )}

      {!loading && error && (
        <div style={{ textAlign: 'center', color: 'var(--danda-color)', padding: '2rem' }}>
          ⚠️ {error}
        </div>
      )}

      {!loading && !error && state.planets.length > 0 && (
        <>
          {state.phase === 'home' && (
            <HomeScreen
              planets={state.planets}
              tokens={state.tokens}
              cycleCount={state.cycleCount}
              onStart={startCycle}
            />
          )}

          {state.phase === 'board' && (
            <div className="nc-board-container">
              <CircularBoard
                planets={state.planets}
                activePlanetIdx={state.currentPlanetIdx}
                visitedSet={visitedSet}
                onPlanetClick={(idx) => { if (idx === state.currentPlanetIdx) openChallenge(); }}
              />
              {activePlanet && (
                <div style={{ textAlign: 'center', marginTop: '1rem', color: 'var(--cosmos-muted)' }}>
                  <span style={{ fontSize: '1.5rem' }}>{activePlanet.icon}</span>
                  <strong style={{ color: PLANET_COLORS[activePlanet.number], marginLeft: '.5rem' }}>
                    {activePlanet.name}
                  </strong>
                  {' '}— {activePlanet.title}
                </div>
              )}
              <button className="nc-action-btn" onClick={openChallenge} style={{ marginTop: '1.5rem' }}>
                 {t('nc.button.openChallenge')} ➜
              </button>
            </div>
          )}

          {(state.phase === 'challenge' || state.phase === 'answer') && state.card && activePlanet && (
            <>
              <ChallengeCard
                card={state.card}
                hintVisible={state.hintVisible}
                answerVisible={state.answerVisible}
                onShowHint={() => setState(prev => ({ ...prev, hintVisible: true }))}
                onShowAnswer={() => setState(prev => ({ ...prev, answerVisible: true }))}
                onRate={handleRate}
              />
              {state.phase === 'answer' && (
                <div style={{ textAlign: 'center', marginTop: '1rem' }}>
                  <button className="nc-action-btn" onClick={handleNext}>
                    {t('nc.button.next')} ➜
                  </button>
                </div>
              )}
            </>
          )}

          {state.phase === 'victory' && (
            <VictoryScreen
              cycleCount={state.cycleCount}
              tokens={state.tokens}
              totalAnswered={state.totalAnswered}
              totalCorrect={state.totalCorrect}
              onContinue={startCycle}
              onHome={() => setState(prev => ({ ...prev, phase: 'home' }))}
            />
          )}
        </>
      )}
    </div>
  );
}
